import { useCallback } from 'react';

import LogoHorizontal from '@assets/logo-horizontal.png';
import { Button } from '@components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@components/ui/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@components/ui/form';
import { Input } from '@components/ui/input';
import { pages } from '@constants/pages';
import errorsValidation from '@errors/validation';
import { zodResolver } from '@hookform/resolvers/zod';
import { type ISendMailForgotPasswordSessionInputData } from '@services/sessions/dtos/sendMailForgotPasswordSession/InputData';
import SessionService from '@services/sessions/SessionService';
import { useMutation } from '@tanstack/react-query';
import { LoaderCircle } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router';
import { toast } from 'sonner';
import { z } from 'zod';

const formSchema = z.object({
  email: z.email(errorsValidation.email).min(1, {
    message: errorsValidation.required,
  }),
});

function ForgotPassword() {
  const navigate = useNavigate();
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    mode: 'onBlur',
    defaultValues: {
      email: '',
    },
  });

  const { isPending, mutateAsync } = useMutation<
    void,
    unknown,
    ISendMailForgotPasswordSessionInputData
  >({
    mutationFn: SessionService.sendMailForgotPassword,
    onError: () => {
      toast.error('Atenção', {
        description: 'Não foi possível enviar o e-mail. Tente novamente',
      });
    },
    onSuccess: () => {
      navigate(pages.auth.signIn, { replace: true });
      toast.error('E-mail enviado com sucesso!');
    },
  });

  const handleSubmit = useCallback(
    async (values: z.infer<typeof formSchema>) => {
      await mutateAsync({
        email: values.email,
      });
    },
    [mutateAsync],
  );

  return (
    <main className="bg-primary flex h-screen w-full flex-col items-center justify-center px-4">
      <Card className="border-background bg-primary w-full max-w-md">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)}>
            <CardHeader className="space-y-6">
              <img
                alt="Logo GPMED"
                className="mx-auto mb-2 w-3/4"
                src={LogoHorizontal}
              />
              <CardTitle className="text-background text-3xl font-semibold">
                Esqueceu sua senha?
              </CardTitle>
              <CardDescription className="text-background text-base">
                Informe seu e-mail e clique em "Recuperar senha". Logo após será
                enviado o link para redefinir a senha no seu WhatsApp.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex flex-row justify-between">
                        <FormLabel className="text-background">
                          E-mail
                        </FormLabel>
                        <FormMessage />
                      </div>
                      <FormControl>
                        <Input
                          className="[&_input]:border-background [&_input]:bg-primary [&_input]:text-background"
                          {...field}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>
            </CardContent>
            <CardFooter className="grid">
              <Button
                className="cursor-pointer"
                disabled={isPending}
                type="submit"
                variant="secondary">
                {isPending ? (
                  <LoaderCircle className="text-background animate-spin" />
                ) : (
                  'RECUPERAR SENHA'
                )}
              </Button>
              <a
                className="text-background mt-4 block text-center text-sm font-light underline"
                href="https://api.whatsapp.com/send?phone=5517996261009"
                target="_blank">
                Precisa de ajuda? Clique aqui e entre em contato com o suporte
              </a>
              <Link
                className="text-background mt-4 text-center text-base tracking-wider underline"
                to={pages.auth.signIn}>
                Voltar
              </Link>
            </CardFooter>
          </form>
        </Form>
      </Card>
    </main>
  );
}

export default ForgotPassword;
